<template>
  <div class="client-pdf-scanner">
    <div class="card shadow-sm">
      <div class="card-header bg-light d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">
            <i class="fas fa-building me-2 text-primary"></i>
            Per-Client PDF Scanner
          </h5>
          <small class="text-muted">Scan all PDF documents for each client</small>
        </div>
        <div>
          <button class="btn btn-outline-secondary me-2" @click="showJobHistory = true">
            <i class="fas fa-history me-1"></i>
            Job History
          </button>
          <button class="btn btn-outline-success me-2" @click="exportSummary" :disabled="loading">
            <i class="fas fa-download me-1"></i>
            Export Summary
          </button>
        </div>
      </div>

      <div class="card-body">
        <!-- Active Job Banner -->
        <div v-if="activeJob" class="alert mb-3" :class="activeJobAlertClass">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <h6 class="alert-heading mb-1">
                <i :class="activeJobIcon" class="me-2"></i>
                {{ activeJob.name }}
                <span class="badge ms-2" :class="activeJobStatusBadge">{{ activeJob.status }}</span>
              </h6>
              <div class="mb-2">
                <strong>Progress:</strong> {{ activeJob.processed_files }} / {{ activeJob.total_files }} files
                ({{ activeJob.progress }}%)
                <span v-if="activeJob.current_file_name" class="text-muted ms-2">
                  - {{ truncateFilename(activeJob.current_file_name, 40) }}
                </span>
              </div>
              <div class="progress mb-2" style="height: 20px;">
                <div
                  class="progress-bar progress-bar-striped"
                  :class="activeJob.status === 'running' ? 'progress-bar-animated bg-primary' : 'bg-secondary'"
                  :style="{ width: activeJob.progress + '%' }"
                >
                  {{ activeJob.progress }}%
                </div>
              </div>
              <div class="row small">
                <div class="col-auto">
                  <span class="badge bg-success">{{ activeJob.no_issues_count }}</span> No Issues
                </div>
                <div class="col-auto">
                  <span class="badge bg-warning text-dark">{{ activeJob.issues_found_count }}</span> Issues
                </div>
                <div class="col-auto">
                  <span class="badge bg-danger">{{ activeJob.not_tagged_count }}</span> Not Tagged
                </div>
                <div class="col-auto">
                  <span class="badge bg-danger">{{ activeJob.scanned_image_count }}</span> Scanned Img
                </div>
                <div class="col-auto">
                  <span class="badge bg-secondary">{{ activeJob.failed_count }}</span> Failed
                </div>
                <div class="col-auto text-muted">
                  <i class="fas fa-clock me-1"></i>{{ activeJob.elapsed_formatted }}
                </div>
              </div>
            </div>
            <div class="ms-3">
              <div v-if="activeJob.status === 'running'">
                <button class="btn btn-warning btn-sm me-1" @click="pauseJob" :disabled="jobActionLoading">
                  <i class="fas fa-pause me-1"></i>
                  Pause
                </button>
                <button class="btn btn-danger btn-sm" @click="cancelJob" :disabled="jobActionLoading">
                  <i class="fas fa-stop me-1"></i>
                  Cancel
                </button>
              </div>
              <div v-else-if="activeJob.status === 'paused'">
                <button class="btn btn-success btn-sm me-1" @click="resumeJob" :disabled="jobActionLoading">
                  <i class="fas fa-play me-1"></i>
                  Resume
                </button>
                <button class="btn btn-danger btn-sm" @click="cancelJob" :disabled="jobActionLoading">
                  <i class="fas fa-stop me-1"></i>
                  Cancel
                </button>
              </div>
              <div v-else-if="activeJob.status === 'pending'">
                <span class="text-muted">Starting...</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Search and Actions Row -->
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text">
                <i class="fas fa-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search clients..."
                v-model="searchQuery"
                @input="debouncedSearch"
              />
              <button
                v-if="searchQuery"
                class="btn btn-outline-secondary"
                type="button"
                @click="clearSearch"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div class="col-md-6 text-end d-flex align-items-center justify-content-end gap-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="forceRescan"
                v-model="forceRescan"
              >
              <label class="form-check-label" for="forceRescan">
                Force Re-scan
              </label>
            </div>
            <button
              class="btn btn-primary"
              :disabled="selectedClients.length === 0 || hasActiveJob"
              @click="startScanJob"
            >
              <i class="fas fa-search me-1"></i>
              Scan Selected ({{ selectedClients.length }})
            </button>
          </div>
        </div>

        <!-- Selection Info -->
        <div v-if="selectedClients.length > 0" class="alert alert-info py-2 mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <span>
              <i class="fas fa-check-square me-2"></i>
              {{ selectedClients.length }} client(s) selected
              ({{ getTotalPdfsForSelected() }} PDFs total)
            </span>
            <button class="btn btn-sm btn-outline-secondary" @click="clearSelection">
              Clear Selection
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Loading clients...</p>
        </div>

        <!-- Data Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 40px;">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isAllSelected"
                    :indeterminate.prop="isPartiallySelected"
                    @change="toggleSelectAll"
                  />
                </th>
                <th @click="sortBy('name')" class="sortable">
                  Client Name
                  <i v-if="sortColumn === 'name'" :class="sortIcon"></i>
                </th>
                <th @click="sortBy('total_pdfs')" class="sortable text-center" style="width: 100px;">
                  Total PDFs
                  <i v-if="sortColumn === 'total_pdfs'" :class="sortIcon"></i>
                </th>
                <th @click="sortBy('scanned_count')" class="sortable text-center" style="width: 100px;">
                  Scanned
                  <i v-if="sortColumn === 'scanned_count'" :class="sortIcon"></i>
                </th>
                <th class="text-center" style="width: 120px;">No Issues</th>
                <th class="text-center" style="width: 120px;">Issues Found</th>
                <th class="text-center" style="width: 100px;">Not Tagged</th>
                <th class="text-center" style="width: 110px;">Scanned Img</th>
                <th @click="sortBy('last_scanned_at')" class="sortable" style="width: 140px;">
                  Last Scan
                  <i v-if="sortColumn === 'last_scanned_at'" :class="sortIcon"></i>
                </th>
                <th style="width: 150px;" class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="client in clients" :key="client.id" :class="{ 'table-active': isSelected(client.id) }">
                <td>
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isSelected(client.id)"
                    @change="toggleSelect(client)"
                  />
                </td>
                <td>
                  <strong>{{ client.name }}</strong>
                </td>
                <td class="text-center">
                  <span class="badge bg-primary">{{ client.total_pdfs }}</span>
                </td>
                <td class="text-center">
                  <span v-if="client.scanned_count > 0" class="badge bg-success">
                    {{ client.scanned_count }}
                  </span>
                  <span v-else class="text-muted">0</span>
                  <small v-if="client.not_scanned_count > 0" class="text-muted d-block">
                    ({{ client.not_scanned_count }} pending)
                  </small>
                </td>
                <td class="text-center">
                  <span v-if="client.no_issues_count > 0" class="badge bg-success">
                    {{ client.no_issues_count }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span v-if="client.issues_found_count > 0" class="badge bg-warning text-dark">
                    {{ client.issues_found_count }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span v-if="client.not_tagged_count > 0" class="badge bg-danger">
                    {{ client.not_tagged_count }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span v-if="client.scanned_image_count > 0" class="badge bg-danger">
                    {{ client.scanned_image_count }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <small v-if="client.last_scanned_at" class="text-muted">
                    {{ formatDate(client.last_scanned_at) }}
                  </small>
                  <small v-else class="text-muted">Never</small>
                </td>
                <td class="text-center">
                  <button
                    class="btn btn-sm btn-outline-primary me-1"
                    @click="scanSingleClient(client)"
                    :disabled="hasActiveJob"
                    title="Scan all PDFs"
                  >
                    <i class="fas fa-search"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-info me-1"
                    @click="viewClientPdfs(client)"
                    title="View PDFs"
                  >
                    <i class="fas fa-list"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-success me-1"
                    @click="exportClient(client.id)"
                    title="Export CSV"
                  >
                    <i class="fas fa-download"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-warning"
                    @click="exportClientIssues(client.id)"
                    title="Export Issues Report"
                    :disabled="client.issues_found_count === 0"
                  >
                    <i class="fas fa-file-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && clients.length === 0" class="text-center py-5">
          <i class="fas fa-building fa-3x text-muted mb-3"></i>
          <p class="text-muted">No clients found with PDF files.</p>
        </div>
      </div>
    </div>

    <!-- Job History Modal -->
    <div
      v-if="showJobHistory"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.5);"
      @click.self="showJobHistory = false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-history me-2"></i>
              Scan Job History
            </h5>
            <button type="button" class="btn-close" @click="showJobHistory = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingJobHistory" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
            </div>
            <div v-else-if="jobHistory.length === 0" class="text-center py-4 text-muted">
              No scan jobs found.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Job Name</th>
                    <th style="width: 100px;">Status</th>
                    <th style="width: 120px;">Progress</th>
                    <th style="width: 150px;">Results</th>
                    <th style="width: 120px;">Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="job in jobHistory" :key="job.id">
                    <td>{{ job.name }}</td>
                    <td>
                      <span class="badge" :class="getJobStatusBadge(job.status)">
                        {{ job.status }}
                      </span>
                    </td>
                    <td>
                      {{ job.processed_files }} / {{ job.total_files }}
                      <small class="text-muted">({{ getJobProgress(job) }}%)</small>
                    </td>
                    <td>
                      <small>
                        <span class="text-success">{{ job.no_issues_count }}</span> /
                        <span class="text-warning">{{ job.issues_found_count }}</span> /
                        <span class="text-danger">{{ job.not_tagged_count }}</span> /
                        <span class="text-secondary">{{ job.failed_count }}</span>
                      </small>
                    </td>
                    <td>
                      <small class="text-muted">{{ formatDate(job.created_at) }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showJobHistory = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Client PDFs Modal -->
    <div
      v-if="showClientPdfsModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.5);"
      @click.self="closeClientPdfsModal"
    >
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-file-pdf me-2"></i>
              {{ viewingClient ? viewingClient.name : '' }} - PDF Files
            </h5>
            <button type="button" class="btn-close" @click="closeClientPdfsModal"></button>
          </div>
          <div class="modal-body">
            <!-- Status Filter -->
            <div class="mb-3">
              <div class="btn-group" role="group">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="clientPdfFilter === '' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="clientPdfFilter = ''; loadClientPdfResults()"
                >
                  All
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="clientPdfFilter === 'no_issues' ? 'btn-success' : 'btn-outline-success'"
                  @click="clientPdfFilter = 'no_issues'; loadClientPdfResults()"
                >
                  No Issues
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="clientPdfFilter === 'issues' ? 'btn-warning' : 'btn-outline-warning'"
                  @click="clientPdfFilter = 'issues'; loadClientPdfResults()"
                >
                  Issues Found
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="clientPdfFilter === 'not_tagged' ? 'btn-danger' : 'btn-outline-danger'"
                  @click="clientPdfFilter = 'not_tagged'; loadClientPdfResults()"
                >
                  Not Tagged
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="clientPdfFilter === 'not_scanned' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="clientPdfFilter = 'not_scanned'; loadClientPdfResults()"
                >
                  Not Scanned
                </button>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loadingClientPdfs" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
            </div>

            <!-- PDFs Table -->
            <div v-else class="table-responsive" style="max-height: 500px;">
              <table class="table table-sm table-hover">
                <thead class="table-light sticky-top">
                  <tr>
                    <th>File Name</th>
                    <th style="width: 80px;">Size</th>
                    <th style="width: 130px;">ADA Status</th>
                    <th style="width: 80px;">Issues</th>
                    <th style="width: 100px;">Scanned</th>
                    <th style="width: 60px;">Link</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pdf in clientPdfs" :key="pdf.id">
                    <td>{{ truncateFilename(pdf.name, 50) }}</td>
                    <td><small class="text-muted">{{ pdf.size_formatted }}</small></td>
                    <td>
                      <span v-if="pdf.scan_status === 'scanned'" class="badge" :class="getPdfStatusClass(pdf)">
                        {{ getPdfStatusLabel(pdf) }}
                      </span>
                      <span v-else class="badge bg-secondary">Not Scanned</span>
                    </td>
                    <td class="text-center">
                      <span v-if="pdf.scan_status === 'scanned'">
                        <span v-if="pdf.critical_issues > 0" class="badge bg-danger me-1">{{ pdf.critical_issues }}</span>
                        <span v-if="pdf.major_issues > 0" class="badge bg-warning text-dark me-1">{{ pdf.major_issues }}</span>
                        <span v-if="pdf.minor_issues > 0" class="badge bg-info">{{ pdf.minor_issues }}</span>
                        <span v-if="pdf.critical_issues === 0 && pdf.major_issues === 0 && pdf.minor_issues === 0">0</span>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <small v-if="pdf.scanned_at" class="text-muted">{{ formatDate(pdf.scanned_at) }}</small>
                      <small v-else class="text-muted">-</small>
                    </td>
                    <td class="text-center">
                      <a :href="pdf.url" target="_blank" class="btn btn-sm btn-outline-secondary" title="Open PDF">
                        <i class="fas fa-external-link-alt"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!loadingClientPdfs && clientPdfs.length === 0" class="text-center py-4 text-muted">
              No PDFs found matching the filter.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeClientPdfsModal">Close</button>
            <button
              type="button"
              class="btn btn-warning"
              @click="exportClientIssues(viewingClient.id)"
            >
              <i class="fas fa-file-alt me-1"></i>
              Export Issues Report
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="exportClient(viewingClient.id)"
            >
              <i class="fas fa-download me-1"></i>
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ClientPdfScanner',

  data() {
    return {
      clients: [],
      loading: true,
      searchQuery: '',
      sortColumn: 'name',
      sortDirection: 'asc',
      selectedClients: [],
      searchTimeout: null,

      // Background job state
      activeJob: null,
      hasActiveJob: false,
      jobPollingInterval: null,
      jobActionLoading: false,
      forceRescan: false,

      // Job history
      showJobHistory: false,
      jobHistory: [],
      loadingJobHistory: false,

      // Client PDFs modal
      showClientPdfsModal: false,
      viewingClient: null,
      clientPdfs: [],
      loadingClientPdfs: false,
      clientPdfFilter: '',
    }
  },

  computed: {
    sortIcon() {
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },

    isAllSelected() {
      return this.clients.length > 0 && this.clients.every(c => this.isSelected(c.id))
    },

    isPartiallySelected() {
      const selectedCount = this.clients.filter(c => this.isSelected(c.id)).length
      return selectedCount > 0 && selectedCount < this.clients.length
    },

    activeJobAlertClass() {
      if (!this.activeJob) return 'alert-info'
      switch (this.activeJob.status) {
        case 'running': return 'alert-primary'
        case 'paused': return 'alert-warning'
        case 'pending': return 'alert-info'
        default: return 'alert-secondary'
      }
    },

    activeJobIcon() {
      if (!this.activeJob) return 'fas fa-spinner'
      switch (this.activeJob.status) {
        case 'running': return 'fas fa-spinner fa-spin'
        case 'paused': return 'fas fa-pause-circle'
        case 'pending': return 'fas fa-clock'
        default: return 'fas fa-check-circle'
      }
    },

    activeJobStatusBadge() {
      if (!this.activeJob) return 'bg-secondary'
      switch (this.activeJob.status) {
        case 'running': return 'bg-primary'
        case 'paused': return 'bg-warning text-dark'
        case 'pending': return 'bg-info'
        default: return 'bg-secondary'
      }
    },
  },

  mounted() {
    this.loadClients()
    this.checkActiveJob()
  },

  beforeDestroy() {
    this.stopJobPolling()
  },

  watch: {
    showJobHistory(val) {
      if (val) {
        this.loadJobHistory()
      }
    },
  },

  methods: {
    async loadClients() {
      this.loading = true
      try {
        const response = await api.get('/juniper/ada-auditor/pdf/clients', {
          params: {
            sort_by: this.sortColumn,
            sort_dir: this.sortDirection,
            search: this.searchQuery,
          },
        })

        if (response.data.success) {
          this.clients = response.data.data
        }
      } catch (error) {
        console.error('Failed to load clients:', error)
        if (this.$toast) this.$toast.error('Failed to load clients')
      } finally {
        this.loading = false
      }
    },

    async checkActiveJob() {
      try {
        const response = await api.get('/juniper/ada-auditor/pdf/jobs/active')
        if (response.data.success && response.data.has_active_job) {
          this.activeJob = response.data.job
          this.hasActiveJob = true
          this.startJobPolling()
        } else {
          this.activeJob = null
          this.hasActiveJob = false
          this.stopJobPolling()
        }
      } catch (error) {
        console.error('Failed to check active job:', error)
      }
    },

    startJobPolling() {
      this.stopJobPolling()
      this.jobPollingInterval = setInterval(() => {
        this.pollJobStatus()
      }, 2000) // Poll every 2 seconds
    },

    stopJobPolling() {
      if (this.jobPollingInterval) {
        clearInterval(this.jobPollingInterval)
        this.jobPollingInterval = null
      }
    },

    async pollJobStatus() {
      if (!this.activeJob) return

      try {
        const response = await api.get(`/juniper/ada-auditor/pdf/jobs/${this.activeJob.id}`)
        if (response.data.success) {
          const job = response.data.job
          this.activeJob = job

          // Check if job finished
          if (['completed', 'cancelled', 'failed'].includes(job.status)) {
            this.stopJobPolling()
            this.hasActiveJob = false

            // Show notification
            if (job.status === 'completed') {
              if (this.$toast) this.$toast.success('Scan job completed!')
            } else if (job.status === 'failed') {
              if (this.$toast) this.$toast.error('Scan job failed: ' + (job.last_error || 'Unknown error'))
            }

            // Refresh client list
            this.loadClients()

            // Keep showing job summary for a moment then clear
            setTimeout(() => {
              this.activeJob = null
            }, 5000)
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error)
      }
    },

    async startScanJob() {
      if (this.selectedClients.length === 0) return

      try {
        const clientIds = this.selectedClients.map(c => c.id)
        const response = await api.post('/juniper/ada-auditor/pdf/jobs', {
          client_ids: clientIds,
          force_rescan: this.forceRescan,
        })

        if (response.data.success) {
          if (this.$toast) this.$toast.success('Scan job started')
          this.clearSelection()
          this.checkActiveJob()
        } else {
          if (this.$toast) this.$toast.error(response.data.message || 'Failed to start scan job')
        }
      } catch (error) {
        console.error('Failed to start scan job:', error)
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to start scan job'
        if (this.$toast) this.$toast.error(message)
      }
    },

    async scanSingleClient(client) {
      this.selectedClients = [client]
      await this.startScanJob()
    },

    async pauseJob() {
      if (!this.activeJob) return
      this.jobActionLoading = true

      try {
        const response = await api.post(`/juniper/ada-auditor/pdf/jobs/${this.activeJob.id}/pause`)
        if (response.data.success) {
          if (this.$toast) this.$toast.info('Job paused')
          this.activeJob.status = 'paused'
        } else {
          if (this.$toast) this.$toast.error(response.data.message || 'Failed to pause job')
        }
      } catch (error) {
        console.error('Failed to pause job:', error)
        if (this.$toast) this.$toast.error('Failed to pause job')
      } finally {
        this.jobActionLoading = false
      }
    },

    async resumeJob() {
      if (!this.activeJob) return
      this.jobActionLoading = true

      try {
        const response = await api.post(`/juniper/ada-auditor/pdf/jobs/${this.activeJob.id}/resume`)
        if (response.data.success) {
          if (this.$toast) this.$toast.info('Job resumed')
          this.checkActiveJob()
        } else {
          if (this.$toast) this.$toast.error(response.data.message || 'Failed to resume job')
        }
      } catch (error) {
        console.error('Failed to resume job:', error)
        if (this.$toast) this.$toast.error('Failed to resume job')
      } finally {
        this.jobActionLoading = false
      }
    },

    async cancelJob() {
      if (!this.activeJob) return

      if (!confirm('Are you sure you want to cancel this scan job?')) return

      this.jobActionLoading = true

      try {
        const response = await api.post(`/juniper/ada-auditor/pdf/jobs/${this.activeJob.id}/cancel`)
        if (response.data.success) {
          if (this.$toast) this.$toast.warning('Job cancelled')
          this.stopJobPolling()
          this.hasActiveJob = false
          this.activeJob = null
          this.loadClients()
        } else {
          if (this.$toast) this.$toast.error(response.data.message || 'Failed to cancel job')
        }
      } catch (error) {
        console.error('Failed to cancel job:', error)
        if (this.$toast) this.$toast.error('Failed to cancel job')
      } finally {
        this.jobActionLoading = false
      }
    },

    async loadJobHistory() {
      this.loadingJobHistory = true
      try {
        const response = await api.get('/juniper/ada-auditor/pdf/jobs', {
          params: { per_page: 20 },
        })
        if (response.data.success) {
          this.jobHistory = response.data.data
        }
      } catch (error) {
        console.error('Failed to load job history:', error)
        if (this.$toast) this.$toast.error('Failed to load job history')
      } finally {
        this.loadingJobHistory = false
      }
    },

    getJobStatusBadge(status) {
      switch (status) {
        case 'completed': return 'bg-success'
        case 'running': return 'bg-primary'
        case 'paused': return 'bg-warning text-dark'
        case 'pending': return 'bg-info'
        case 'cancelled': return 'bg-secondary'
        case 'failed': return 'bg-danger'
        default: return 'bg-secondary'
      }
    },

    getJobProgress(job) {
      if (!job || job.total_files === 0) return 0
      return Math.round((job.processed_files / job.total_files) * 100)
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
      this.loadClients()
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadClients()
      }, 300)
    },

    clearSearch() {
      this.searchQuery = ''
      this.loadClients()
    },

    isSelected(clientId) {
      return this.selectedClients.some(c => c.id === clientId)
    },

    toggleSelect(client) {
      const index = this.selectedClients.findIndex(c => c.id === client.id)
      if (index === -1) {
        this.selectedClients.push(client)
      } else {
        this.selectedClients.splice(index, 1)
      }
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.clients.forEach(client => {
          const index = this.selectedClients.findIndex(c => c.id === client.id)
          if (index !== -1) {
            this.selectedClients.splice(index, 1)
          }
        })
      } else {
        this.clients.forEach(client => {
          if (!this.isSelected(client.id)) {
            this.selectedClients.push(client)
          }
        })
      }
    },

    clearSelection() {
      this.selectedClients = []
    },

    getTotalPdfsForSelected() {
      return this.selectedClients.reduce((sum, c) => sum + c.total_pdfs, 0)
    },

    async viewClientPdfs(client) {
      this.viewingClient = client
      this.showClientPdfsModal = true
      this.clientPdfFilter = ''
      await this.loadClientPdfResults()
    },

    async loadClientPdfResults() {
      if (!this.viewingClient) return

      this.loadingClientPdfs = true
      try {
        const response = await api.get(`/juniper/ada-auditor/pdf/clients/${this.viewingClient.id}/results`, {
          params: {
            status: this.clientPdfFilter || undefined,
            per_page: 100,
          },
        })

        if (response.data.success) {
          this.clientPdfs = response.data.data
        }
      } catch (error) {
        console.error('Failed to load client PDFs:', error)
        if (this.$toast) this.$toast.error('Failed to load PDF list')
      } finally {
        this.loadingClientPdfs = false
      }
    },

    closeClientPdfsModal() {
      this.showClientPdfsModal = false
      this.viewingClient = null
      this.clientPdfs = []
    },

    async exportSummary() {
      try {
        const response = await api.get('/juniper/ada-auditor/pdf/export/summary', {
          responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'pdf-audit-summary-' + new Date().toISOString().split('T')[0] + '.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        if (this.$toast) this.$toast.success('Summary exported')
      } catch (error) {
        console.error('Failed to export summary:', error)
        if (this.$toast) this.$toast.error('Failed to export summary')
      }
    },

    async exportClient(clientId) {
      try {
        const response = await api.get(`/juniper/ada-auditor/pdf/export/client/${clientId}`, {
          responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const client = this.clients.find(c => c.id === clientId)
        const clientName = client ? client.name.replace(/[^a-zA-Z0-9_-]/g, '_') : clientId
        link.setAttribute('download', 'pdf-audit-' + clientName + '-' + new Date().toISOString().split('T')[0] + '.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        if (this.$toast) this.$toast.success('Export downloaded')
      } catch (error) {
        console.error('Failed to export client PDFs:', error)
        if (this.$toast) this.$toast.error('Failed to export')
      }
    },

    async exportClientIssues(clientId) {
      try {
        const response = await api.get(`/juniper/ada-auditor/pdf/export/client/${clientId}/issues`, {
          responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const client = this.clients.find(c => c.id === clientId)
        const clientName = client ? client.name.replace(/[^a-zA-Z0-9_-]/g, '_') : clientId
        link.setAttribute('download', 'pdf-issues-report-' + clientName + '-' + new Date().toISOString().split('T')[0] + '.txt')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        if (this.$toast) this.$toast.success('Issues report downloaded')
      } catch (error) {
        console.error('Failed to export client issues:', error)
        const message = error.response?.data?.message || 'Failed to export issues report'
        if (this.$toast) this.$toast.error(message)
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    },

    truncateFilename(name, maxLength) {
      if (!name) return ''
      if (name.length <= maxLength) return name
      const ext = name.split('.').pop()
      const base = name.slice(0, -(ext.length + 1))
      const truncatedBase = base.slice(0, maxLength - ext.length - 4) + '...'
      return truncatedBase + '.' + ext
    },

    getPdfStatusLabel(pdf) {
      if (pdf.is_scanned_image && !pdf.is_tagged) {
        return 'Scanned Image'
      }
      if (pdf.is_tagged === false) {
        return 'Not Tagged'
      }
      const totalIssues = (pdf.critical_issues || 0) + (pdf.major_issues || 0) + (pdf.minor_issues || 0)
      if (totalIssues === 0) {
        return 'No Issues Found'
      }
      return totalIssues + ' Issue' + (totalIssues > 1 ? 's' : '') + ' Found'
    },

    getPdfStatusClass(pdf) {
      if ((pdf.is_scanned_image && !pdf.is_tagged) || pdf.is_tagged === false) {
        return 'bg-danger'
      }
      if ((pdf.critical_issues || 0) > 0) {
        return 'bg-danger'
      }
      if ((pdf.major_issues || 0) > 0) {
        return 'bg-warning text-dark'
      }
      if ((pdf.minor_issues || 0) > 0) {
        return 'bg-info'
      }
      return 'bg-success'
    },
  },
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef;
}

.sortable i {
  margin-left: 5px;
  font-size: 0.75rem;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f8f9fa;
}

.modal.show {
  display: block;
}
</style>
